import type { ReactNode } from 'react';

export const statePanelStates = [
  'loading',
  'empty',
  'blocked',
  'failed',
  'denied',
  'stale',
  'unavailable',
] as const;

export type StatePanelState = (typeof statePanelStates)[number];

export const statePanelRecoveryKinds = {
  loading: ['cancel'],
  empty: ['create', 'add', 'import', 'change-scope'],
  blocked: ['open-owner', 'view-requirements', 'recheck', 'audited-override'],
  failed: ['retry', 'resume', 'return-safe'],
  denied: ['switch-scope', 'request-access', 'return-landing'],
  stale: ['refresh', 'open-diagnostics'],
  unavailable: [
    'recheck-availability',
    'open-diagnostics',
    'enable',
    'connect',
    'return-safe',
  ],
} as const;

type ButtonTarget = {
  onAction: () => void;
  href?: never;
  isLoading?: boolean;
};

type LinkTarget = {
  href: string;
  onAction?: never;
  isLoading?: never;
};

type RecoveryTarget = ButtonTarget | LinkTarget;

type RecoveryAction<Kind extends string, Proof extends object = object> = {
  kind: Kind;
  disabled?: boolean;
} & Proof &
  RecoveryTarget;

export type LoadingRecovery = RecoveryAction<'cancel', { cancellable: true }>;

export type EmptyRecovery =
  | RecoveryAction<'create' | 'add' | 'import', { authorized: true }>
  | RecoveryAction<'change-scope'>;

export type BlockedRecovery =
  | RecoveryAction<'open-owner' | 'view-requirements' | 'recheck'>
  | RecoveryAction<
      'audited-override',
      { backendSupported: true; authorized: true; audited: true }
    >;

export type FailedRecovery =
  | RecoveryAction<'retry', { safeToRetry: true }>
  | RecoveryAction<'resume', { runLabel: string }>
  | RecoveryAction<'return-safe', { destination: string }>;

export type DeniedRecovery =
  | RecoveryAction<'switch-scope' | 'request-access'>
  | RecoveryAction<'return-landing', { destination: string }>;

export type StaleRecovery = RecoveryAction<'refresh' | 'open-diagnostics'>;

export type UnavailableRecovery =
  | RecoveryAction<'recheck-availability' | 'open-diagnostics'>
  | RecoveryAction<'enable' | 'connect', { authorized: true }>
  | RecoveryAction<'return-safe', { destination: string }>;

interface StatePanelBase {
  additionalContext?: ReactNode;
  announce?: boolean;
  className?: string;
  technicalDetails?: ReactNode;
}

export interface LoadingStatePanelProps extends StatePanelBase {
  state: 'loading';
  objectName: string;
  taskContext: string;
  recovery?: LoadingRecovery;
}

export interface EmptyStatePanelProps extends StatePanelBase {
  state: 'empty';
  objectNamePlural: string;
  objectNameSingular: string;
  activeScope: string;
  recovery?: EmptyRecovery;
}

export interface BlockedStatePanelProps extends StatePanelBase {
  state: 'blocked';
  blockedAction: string;
  prerequisite: string;
  owner: string;
  evidenceToClear: string;
  recovery?: BlockedRecovery;
}

export interface FailedStatePanelProps extends StatePanelBase {
  state: 'failed';
  failedAction: string;
  outcome: string;
  safeRetryBoundary: string;
  recovery?: FailedRecovery;
}

export interface DeniedStatePanelProps extends StatePanelBase {
  state: 'denied';
  deniedAction: string;
  activeScope: string;
  accessOwner: string;
  recovery?: DeniedRecovery;
}

export interface StaleStatePanelProps extends StatePanelBase {
  state: 'stale';
  objectName: string;
  observedAt: string;
  freshnessExpectation: string;
  actionRestriction: string;
  recovery?: StaleRecovery;
}

export type UnavailableReason =
  | 'disabled'
  | 'disconnected'
  | 'unsupported'
  | 'unreachable';

export interface UnavailableStatePanelProps extends StatePanelBase {
  state: 'unavailable';
  objectName: string;
  dependency: string;
  reason: UnavailableReason;
  ownerOrRecheckCondition: string;
  recovery?: UnavailableRecovery;
}

export type StatePanelProps =
  | LoadingStatePanelProps
  | EmptyStatePanelProps
  | BlockedStatePanelProps
  | FailedStatePanelProps
  | DeniedStatePanelProps
  | StaleStatePanelProps
  | UnavailableStatePanelProps;

export interface StatePanelPresentation {
  context: Array<{ label: string; value: string }>;
  description: string;
  recoveryLabel?: string;
  title: string;
}

const assertRecoveryContract = (props: StatePanelProps): void => {
  const recovery = props.recovery;
  if (!recovery) return;

  const hasHref = typeof recovery.href === 'string' && recovery.href.length > 0;
  const hasHandler = typeof recovery.onAction === 'function';
  if (hasHref === hasHandler) {
    throw new Error('A recovery action requires exactly one non-empty href or handler.');
  }

  const allowed = statePanelRecoveryKinds[props.state] as readonly string[];
  if (!allowed.includes(recovery.kind)) {
    throw new Error(
      `Recovery "${recovery.kind}" is not allowed for StatePanel state "${props.state}".`,
    );
  }

  if (recovery.kind === 'cancel' && !('cancellable' in recovery && recovery.cancellable)) {
    throw new Error('Loading can be cancelled only with a real cancellation contract.');
  }
  if (recovery.kind === 'retry' && !('safeToRetry' in recovery && recovery.safeToRetry)) {
    throw new Error('Failed operations can be retried only after their outcome is reconciled.');
  }
  if (
    recovery.kind === 'audited-override' &&
    !(
      'backendSupported' in recovery &&
      recovery.backendSupported &&
      'authorized' in recovery &&
      recovery.authorized &&
      'audited' in recovery &&
      recovery.audited
    )
  ) {
    throw new Error('Overrides require backend support, authorization, and audit proof.');
  }
  if (
    (recovery.kind === 'create' ||
      recovery.kind === 'add' ||
      recovery.kind === 'import' ||
      recovery.kind === 'enable' ||
      recovery.kind === 'connect') &&
    !('authorized' in recovery && recovery.authorized)
  ) {
    throw new Error(`Recovery "${recovery.kind}" requires explicit authorization.`);
  }
};

const unavailableRecoveryLabel = (
  props: UnavailableStatePanelProps,
): string | undefined => {
  switch (props.recovery?.kind) {
    case 'recheck-availability':
      return 'Recheck availability';
    case 'open-diagnostics':
      return 'Open diagnostics';
    case 'enable':
      return `Enable ${props.dependency}`;
    case 'connect':
      return `Connect ${props.dependency}`;
    case 'return-safe':
      return `Return to ${props.recovery.destination}`;
    default:
      return undefined;
  }
};

export const getStatePanelPresentation = (
  props: StatePanelProps,
): StatePanelPresentation => {
  assertRecoveryContract(props);

  switch (props.state) {
    case 'loading':
      return {
        title: `Loading ${props.objectName}`,
        description: `Waiting for the first authoritative ${props.objectName} response. The current task stays visible while data resolves.`,
        context: [{ label: 'Current task', value: props.taskContext }],
        recoveryLabel: props.recovery ? 'Cancel' : undefined,
      };
    case 'empty': {
      const recoveryLabel = (() => {
        switch (props.recovery?.kind) {
          case 'create':
            return `Create ${props.objectNameSingular}`;
          case 'add':
            return `Add ${props.objectNameSingular}`;
          case 'import':
            return `Import ${props.objectNamePlural}`;
          case 'change-scope':
            return 'Change scope';
          default:
            return undefined;
        }
      })();
      return {
        title: `No ${props.objectNamePlural} yet`,
        description: `${props.activeScope} loaded successfully and contains no ${props.objectNamePlural}. This is an empty collection, not a zero measurement or filtered result.`,
        context: [{ label: 'Active scope', value: props.activeScope }],
        recoveryLabel,
      };
    }
    case 'blocked': {
      const recoveryLabel = (() => {
        switch (props.recovery?.kind) {
          case 'open-owner':
            return `Open ${props.owner}`;
          case 'view-requirements':
            return 'View requirements';
          case 'recheck':
            return 'Recheck prerequisite';
          case 'audited-override':
            return 'Use audited override';
          default:
            return undefined;
        }
      })();
      return {
        title: `Blocked: ${props.prerequisite}`,
        description: `${props.blockedAction} cannot continue until the named prerequisite changes. Repeating the blocked action will not clear it.`,
        context: [
          { label: 'Blocked action', value: props.blockedAction },
          { label: 'Owner', value: props.owner },
          { label: 'Evidence that clears this block', value: props.evidenceToClear },
        ],
        recoveryLabel,
      };
    }
    case 'failed': {
      const recoveryLabel = (() => {
        switch (props.recovery?.kind) {
          case 'retry':
            return `Retry ${props.failedAction}`;
          case 'resume':
            return `Resume ${props.recovery.runLabel}`;
          case 'return-safe':
            return `Return to ${props.recovery.destination}`;
          default:
            return undefined;
        }
      })();
      return {
        title: `${props.failedAction} failed`,
        description: `${props.failedAction} reached a known non-success result. The persisted outcome and safe recovery boundary remain visible.`,
        context: [
          { label: 'Outcome', value: props.outcome },
          { label: 'Safe retry boundary', value: props.safeRetryBoundary },
        ],
        recoveryLabel,
      };
    }
    case 'denied': {
      const recoveryLabel = (() => {
        switch (props.recovery?.kind) {
          case 'switch-scope':
            return 'Switch scope';
          case 'request-access':
            return 'Request access';
          case 'return-landing':
            return `Return to ${props.recovery.destination}`;
          default:
            return undefined;
        }
      })();
      return {
        title: 'Access denied',
        description: `Your identity is known, but the current role or scope does not authorize ${props.deniedAction}. Access must change before this action can run.`,
        context: [
          { label: 'Active scope', value: props.activeScope },
          { label: 'Access owner', value: props.accessOwner },
        ],
        recoveryLabel,
      };
    }
    case 'stale':
      return {
        title: `Last known ${props.objectName}`,
        description: `The last authoritative ${props.objectName} is older than its freshness contract. It remains visible but is not current.`,
        context: [
          { label: 'Observed at', value: props.observedAt },
          { label: 'Freshness expectation', value: props.freshnessExpectation },
          { label: 'Action restriction', value: props.actionRestriction },
        ],
        recoveryLabel:
          props.recovery?.kind === 'refresh'
            ? `Refresh ${props.objectName}`
            : props.recovery?.kind === 'open-diagnostics'
              ? 'Open diagnostics'
              : undefined,
      };
    case 'unavailable':
      return {
        title: `${props.objectName} unavailable`,
        description: `${props.objectName} cannot be provided because ${props.dependency} is ${props.reason}. No usable prior value is available.`,
        context: [
          { label: 'Dependency', value: props.dependency },
          { label: 'Owner or recheck condition', value: props.ownerOrRecheckCondition },
        ],
        recoveryLabel: unavailableRecoveryLabel(props),
      };
  }
};
